import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { styled } from '@xstyled/styled-components';
import { ImageFieldProps, MediaFile } from '../types';
import { FieldWrapper, Label } from '../sharedComponents';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import { EditBtnIcon } from '~/editor/buttons/EditBtn';

const ImageBox = styled.div`
  width: 100%;
  height: 246px;
  border: 2px dashed #d4d8dd;
  border-radius: 20px;
  margin-bottom: 4px;
  background: #e8e8e8;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImagePreview = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const EditImageButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  margin: 0;
  padding: 5px;
  background-color: #f3ecff;
  color: #9738ff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.18s ease,
    color 0.18s ease;

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
  svg path {
    fill: currentColor !important;
    transition: fill 0.18s ease;
  }

  &:hover {
    background-color: #9738ff;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }

  &.active {
    background-color: #9845fb;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }
`;

const HelperText = styled.p`
  font-family: Raleway;
  font-size: 12px;
  line-height: 16px;
  color: #555;
  margin: 6px 0 10px;
  @media (max-height: 900px) {
    margin: 4px 0 8px;
  }
  @media (max-height: 780px) {
    margin: 2px 0 6px;
  }
`;

const PlaceholderImage = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 80px;
    height: 80px;
    color: #b8b8b8;
  }
`;

const FilePickerPortal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FilePickerBackdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
`;

const FilePickerContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  z-index: 30001;
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  overflow: hidden;
`;

const ImagePlaceholderIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="3"
      y="3"
      width="18"
      height="18"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
    <path
      d="M21 15l-5-5L5 21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ImageField: React.FC<ImageFieldProps> = ({
  imageUrl,
  onImageSelect,
  disabled = false
}) => {
  const [showFilePicker, setShowFilePicker] = useState(false);
  const fileElRef = useRef<HTMLDivElement>(null);

  const handleImageSelect = (file: MediaFile) => {
    onImageSelect(file);
    setShowFilePicker(false);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setShowFilePicker(true);
    }
  };

  return (
    <>
      <FieldWrapper>
        <Label>Card Image</Label>
        <ImageBox ref={fileElRef}>
          {imageUrl ? (
            <ImagePreview src={imageUrl} alt="Card preview" />
          ) : (
            <PlaceholderImage>
              <ImagePlaceholderIcon />
            </PlaceholderImage>
          )}
          <EditImageButton
            type="button"
            onClick={handleEditClick}
            disabled={disabled}
            aria-label="Edit image"
          >
            <EditBtnIcon />
          </EditImageButton>
        </ImageBox>
        <HelperText>
          Upload files up to 20 MB, resolution should be larger than 1000x1000
          px. Accepted file formats: JPG, PNG, GIF
        </HelperText>
      </FieldWrapper>

      {showFilePicker &&
        ReactDOM.createPortal(
          <FilePickerPortal>
            <FilePickerBackdrop onClick={() => setShowFilePicker(false)} />
            <FilePickerContent>
              <FilePickerV2
                fileElRef={fileElRef}
                updateFile={handleImageSelect}
                type="image"
                onClose={() => setShowFilePicker(false)}
              />
            </FilePickerContent>
          </FilePickerPortal>,
          document.body
        )}
    </>
  );
};
